import { HandledErrorTracker } from '@elite/shared/util-http-response';
import { ActionReducer } from '@ngrx/store';

/**
 * Only failure paths ever wrote `error`, so nothing on the way back up ever took it back.
 *
 * The Operator submits, the server names the field it rejected, the banner says so. They fix the
 * field, submit again, the save works — and they are still reading the complaint it disproved,
 * because the `*Success` handler had no reason to think about a field it does not set. 229 of
 * those handlers across 40 reducers were written that way, which is the tell that this is one
 * rule rather than 229 oversights: it belongs in the place every reducer already passes through.
 *
 * {@link HandledErrorTracker} decides what counts as an answer, and records the three stateless
 * rules that look sufficient and are not. This wrapper's own job is only to find the slices an
 * action touched: a slice whose reference did not change did not react, so whatever it is
 * carrying is not something this action can have answered.
 *
 * Note it does **not** filter on the action type. `Success` in the name proved to be neither
 * necessary nor sufficient — `loadOperationAreaDropdownSuccess` has it and answers nothing — so
 * the decision is made entirely from what the slice did.
 *
 * **Why `null` and not `undefined`.** 166 of the 181 reducer initialisers spell the empty error
 * `null`, and several declare the field non-optional (`error: string | null`), where `undefined`
 * would be a value the type does not admit. `null` satisfies every declaration in the workspace.
 *
 * **What it does not do.** The banner still stands for the duration of a retry — it is retired
 * when the retry lands, not when it starts. A reducer that wants the stronger behaviour clears
 * `error` in its trigger handler, the way `initFleets` already does.
 */
export function clearErrorOnSuccessMetaReducer<S>(reducer: ActionReducer<S>): ActionReducer<S> {
  const handledErrors = new HandledErrorTracker();

  return (state, action) => {
    const next = reducer(state, action);
    if (state === undefined || next === state) return next;

    return retireAcrossSlices(handledErrors, state as Record<string, unknown>, next as Record<string, unknown>) as S;
  };
}

/** Copies only the slices that need it, so an action that retires nothing keeps state identity. */
function retireAcrossSlices(
  handledErrors: HandledErrorTracker,
  previous: Record<string, unknown>,
  next: Record<string, unknown>,
): Record<string, unknown> {
  const before = new Map(Object.entries(previous));
  const patched = new Map<string, unknown>();

  for (const [key, after] of Object.entries(next)) {
    const was = before.get(key);
    if (was === after || after === null || typeof after !== 'object' || Array.isArray(after)) continue;

    const retired = handledErrors.retire(
      key,
      was as Record<string, unknown> | undefined,
      after as Record<string, unknown>,
      null,
    );
    if (retired !== after) patched.set(key, retired);
  }

  return patched.size === 0 ? next : { ...next, ...Object.fromEntries(patched) };
}
